/**
 * useEnquiriesState Hook
 * State management hook for the enquiries feature in collins-mobile
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useDiaryContext } from "../../context/DiaryContext";
import { useUserContext } from "../../hooks/useUserContext";
import {
  EnquiriesSortOption,
  Enquiry,
  EnquiryStatus,
  FacetCounts,
  FilterOptions
} from "../../types/enquiries";
import {
  fetchEnquiries,
  takeEnquiryRequest,
  updateEnquiryStatusRequest,
  assignEnquiryRequest,
  acknowledgeBookingNotifications,
  bulkReject,
  bulkDelete,
  bulkAssignTo,
  bulkSetStatus,
  bulkAddLabels,
  bulkRemoveLabels,
  bulkSetFollowUp
} from "./api";
import type { BulkDeleteReason } from "./api";
import type { BulkUpdateFields } from "./Content/components/BulkActionBar/BulkActionBar";
import { fetchFacetCountsFromServer } from "../../APIs/Enquiries/GetEnquiries";
import {
  EnquiriesUrlState,
  getStateFromSearchParams,
  hasEnquiriesParams,
  useEnquiriesSearchParams
} from "./useEnquiriesSearchParams";

const DEFAULT_LIMIT = 15;

const FILTERS_PREF_KEY = "enquiries_filters";
const SORT_PREF_KEY = "enquiries_sort";
const ENQUIRIES_CACHE_KEY = "enquiries_cache";
const ENQUIRIES_CACHE_TIMESTAMP_KEY = "enquiries_cache_timestamp";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Returns the current URL search string. Extracted so it can be mocked in
 * tests where jsdom's Location object is not easily overridable.
 */
export const getLocationSearch = (): string => window.location.search;

/**
 * Virtual notification types are computed at runtime from booking properties
 * and have no embedded notification documents. Keep in sync with
 * Notification::VIRTUAL_TYPES in collins-api.
 */
const VIRTUAL_NOTIFICATION_TYPES = new Set([
  "follow_up",
  "untaken_two_hours",
  "for_today"
]);

/**
 * Maps notification filter values used by the sidebar/URL to the
 * corresponding facet count key in FacetCounts["notifications"].
 * Most types use the same key for both, but some differ.
 */
const NOTIFICATION_FILTER_TO_FACET_KEY: Record<string, string> = {
  follow_up: "follow_up",
  untaken_two_hours: "waiting_2hrs",
  for_today: "todays_enquiries",
  message_received: "new_messages",
  mentioned: "mentioned",
  customer_notes: "customer_notes",
  offer_selected: "offer_selected",
  deposit_authenticated: "deposit_authenticated",
  deposit_paid: "deposit_paid",
  packages_requested: "packages_requested",
  customer_preorder_added: "customer_preorder_added",
  customer_preorder_complete: "customer_preorder_complete",
  email_delivery_failed: "email_delivery_failed",
  customer_change: "customer_change",
  contract_signed: "contract_signed",
  customer_details: "customer_details"
};

interface UseEnquiriesStateReturn {
  enquiries: Enquiry[];
  loading: boolean;
  error: string | null;
  filters: FilterOptions;
  sortOption: EnquiriesSortOption;
  searchQuery: string;
  facetCounts: FacetCounts | null;
  filteredTotalHeads: number;
  total: number;
  offset: number;
  limit: number;
  selectedEnquiry: Enquiry | null;
  recentlyUpdatedIds: string[];
  updateFilter: (key: keyof FilterOptions, value: unknown) => void;
  batchUpdateFilters: (updates: Partial<FilterOptions>) => void;
  clearFilters: () => void;
  updateSort: (sort: EnquiriesSortOption) => void;
  updateSearch: (query: string) => void;
  onPaginate: (newOffset: number) => void;
  takeEnquiry: (enquiryId: string) => Promise<void>;
  updateStatus: (enquiryId: string, status: EnquiryStatus) => Promise<void>;
  assignEnquiry: (
    enquiryId: string,
    userId: string | undefined
  ) => Promise<void>;
  clearNotifications: (selectedIds: Set<string>) => Promise<void>;
  bulkRejectEnquiries: (selectedIds: Set<string>) => Promise<void>;
  bulkDeleteEnquiries: (
    selectedIds: Set<string>,
    reason?: BulkDeleteReason
  ) => Promise<void>;
  bulkAssignEnquiries: (
    selectedIds: Set<string>,
    userId: string | undefined
  ) => Promise<void>;
  bulkApplyUpdates: (
    selectedIds: Set<string>,
    updates: BulkUpdateFields
  ) => Promise<void>;
  bulkActionError: string | null;
  clearBulkActionError: () => void;
  isBulkApplying: boolean;
  refetch: () => Promise<void>;
  setLimit: (limit: number) => void;
  setSelectedEnquiry: (enquiry: Enquiry | null) => void;
}

const getStoredValue = <T>(key: string, defaultValue: T): T => {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored) as T;
    }
  } catch {
    // Ignore parsing errors
  }
  return defaultValue;
};

const setStoredValue = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors
  }
};

interface EnquiriesCache {
  enquiries: Enquiry[];
  total: number;
  facetCounts: FacetCounts | null;
  filteredTotalHeads: number;
}

const getCachedEnquiries = (): EnquiriesCache | null => {
  try {
    const timestamp = sessionStorage.getItem(ENQUIRIES_CACHE_TIMESTAMP_KEY);
    if (!timestamp) return null;

    const cacheAge = Date.now() - parseInt(timestamp, 10);
    if (cacheAge > CACHE_TTL_MS) {
      // Cache expired
      sessionStorage.removeItem(ENQUIRIES_CACHE_KEY);
      sessionStorage.removeItem(ENQUIRIES_CACHE_TIMESTAMP_KEY);
      return null;
    }

    const cached = sessionStorage.getItem(ENQUIRIES_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached) as EnquiriesCache;
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
};

const setCachedEnquiries = (cache: EnquiriesCache): void => {
  try {
    sessionStorage.setItem(ENQUIRIES_CACHE_KEY, JSON.stringify(cache));
    sessionStorage.setItem(
      ENQUIRIES_CACHE_TIMESTAMP_KEY,
      Date.now().toString()
    );
  } catch {
    // Ignore storage errors
  }
};

/**
 * Marks an enquiry as recently updated for visual feedback, then clears after 2 seconds
 */
const markAsRecentlyUpdated = (
  enquiryId: string,
  setRecentlyUpdatedIds: React.Dispatch<React.SetStateAction<string[]>>
): void => {
  setRecentlyUpdatedIds((prev) => [...prev, enquiryId]);
  setTimeout(() => {
    setRecentlyUpdatedIds((prev) => prev.filter((id) => id !== enquiryId));
  }, 2000);
};

export const useEnquiriesState = (): UseEnquiriesStateReturn => {
  const { venues, selectedEnquiry: contextSelectedEnquiry } = useDiaryContext();
  const { user } = useUserContext();
  const userId = user?._id;

  // Track the previous selectedEnquiry to detect changes from SelectedBooking
  const prevSelectedEnquiryRef =
    useRef<typeof contextSelectedEnquiry>(undefined);

  // When filters are initialised directly from URL params we skip the first
  // handleStateFromUrl callback to avoid a redundant state update and duplicate
  // API fetch that would flash a loading skeleton.
  const initializedFromUrlRef = useRef(false);

  // Load cached data on initial mount to avoid loading state when returning from booking view
  const cachedData = useMemo(() => getCachedEnquiries(), []);

  const [enquiries, setEnquiries] = useState<Enquiry[]>(
    cachedData?.enquiries ?? []
  );
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterOptions>(() => {
    const search = getLocationSearch();
    if (hasEnquiriesParams(search)) {
      // Parse filters directly from URL params to prevent a race condition.
      // Previously this returned {} and relied on handleStateFromUrl to
      // hydrate from the URL. Between initialisation and the callback,
      // fetchData would fire with empty filters, briefly returning
      // unfiltered results and causing visible flickering when returning
      // from SelectedBooking with an active status or assigned_to filter.
      const urlState = getStateFromSearchParams(search);
      const urlFilters: FilterOptions = {};
      if (urlState.status) urlFilters.status = urlState.status;
      if (urlState.dateFrom) urlFilters.date_from = urlState.dateFrom;
      if (urlState.dateTo) urlFilters.date_to = urlState.dateTo;
      if (urlState.assignedTo?.length) {
        urlFilters.assigned_to = urlState.assignedTo.join(",");
      }

      initializedFromUrlRef.current = true;

      // For global search, use only URL filters to avoid stale defaults
      if (urlState.query) return urlFilters;

      // For standard deep links, merge with stored defaults
      const stored = getStoredValue<FilterOptions>(FILTERS_PREF_KEY, {});
      return { ...stored, ...urlFilters };
    }
    const stored = getStoredValue<FilterOptions>(FILTERS_PREF_KEY, {});
    // Default to 'me' to match collins-ui behaviour (shows "assigned to me" by default).
    // 'me' is a special value the backend interprets as the current authenticated user.
    if (!stored.assigned_to) {
      stored.assigned_to = "me";
    }
    return stored;
  });
  const [sortOption, setSortOption] = useState<EnquiriesSortOption>(() => {
    const search = getLocationSearch();
    if (hasEnquiriesParams(search)) {
      return getStateFromSearchParams(search).sort;
    }
    return getStoredValue(SORT_PREF_KEY, "date_desc");
  });
  const [searchQuery, setSearchQuery] = useState(() => {
    // Initialise from URL params to prevent a race condition with
    // useEnquiriesSearchParams. Without this, searchQuery starts as ""
    // and the first fetchData call runs without the query filter before
    // the READ effect in useEnquiriesSearchParams can hydrate the state.
    const params = new URLSearchParams(getLocationSearch());
    return params.get("query") ?? "";
  });
  const [facetCounts, setFacetCounts] = useState<FacetCounts | null>(
    cachedData?.facetCounts ?? null
  );
  const [filteredTotalHeads, setFilteredTotalHeads] = useState(
    cachedData?.filteredTotalHeads ?? 0
  );
  const [total, setTotal] = useState(cachedData?.total ?? 0);
  const [offset, setOffset] = useState(() => {
    // Initialise from URL params to prevent a race condition with
    // useEnquiriesSearchParams. When returning from SelectedBooking the URL
    // contains the saved page offset (e.g. ?start=15). Without this, offset
    // starts at 0 and the WRITE effect in useEnquiriesSearchParams overwrites
    // the URL before the READ effect can hydrate the state, causing the page
    // number to flicker and infinite API calls.
    const params = new URLSearchParams(getLocationSearch());
    const start = params.get("start");
    if (start) {
      const parsed = parseInt(start, 10);
      if (!Number.isNaN(parsed) && parsed >= 0) return parsed;
    }
    return 0;
  });
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);
  const [recentlyUpdatedIds, setRecentlyUpdatedIds] = useState<string[]>([]);

  // Deep linking: hydrate state from URL when params are present.
  // This callback is invoked once on mount by useEnquiriesSearchParams.
  const handleStateFromUrl = useCallback((urlState: EnquiriesUrlState) => {
    // When filters were already initialised from URL params during
    // useState, skip this first callback to avoid a redundant state
    // update and duplicate API fetch.
    if (initializedFromUrlRef.current) {
      initializedFromUrlRef.current = false;
      return;
    }

    const isGlobalSearch = Boolean(urlState.query);

    // Build the filter object from the URL parameters.
    const urlFilters: FilterOptions = {};
    if (urlState.status) {
      urlFilters.status = urlState.status;
    }
    if (urlState.dateFrom) {
      urlFilters.date_from = urlState.dateFrom;
    }
    if (urlState.dateTo) {
      urlFilters.date_to = urlState.dateTo;
    }
    if (urlState.assignedTo?.length) {
      urlFilters.assigned_to = urlState.assignedTo.join(",");
    }

    // When navigating from the global search bar (URL contains a query
    // param), replace filters entirely so stale localStorage defaults
    // like assigned_to=me don't restrict results and hide matches.
    // For standard deep links (bookmarks with status/date filters but
    // no query), merge with localStorage defaults to preserve expected
    // filter behaviour such as the default assigned_to=me.
    if (isGlobalSearch) {
      setFilters(urlFilters);
    } else {
      setFilters((prev) => ({ ...prev, ...urlFilters }));
    }

    setSortOption(urlState.sort);
    setOffset(urlState.offset);
    setSearchQuery(urlState.query);
  }, []);

  // Deep linking: keep URL search params in sync with state
  useEnquiriesSearchParams({
    filters,
    sortOption,
    offset,
    searchQuery,
    onStateFromUrl: handleStateFromUrl
  });

  const venueIds = useMemo(() => venues.map((venue) => venue._id), [venues]);

  const fetchData = useCallback(
    async (showLoading = true) => {
      // Wait until userId has resolved before fetching.
      // This value arrives asynchronously after mount, so without this guard
      // it changing would recreate fetchData and re-trigger the effect,
      // causing cascading API calls on first render.
      if (!userId) return;

      // If the user has no venues assigned, set empty state immediately
      // to prevent the page from remaining in an indefinite loading or cached state.
      if (!venueIds.length) {
        setEnquiries([]);
        setTotal(0);
        setFacetCounts(null);
        setLoading(false);
        setError(null);
        return;
      }

      // Only show loading state if requested and we don't have existing data
      // This prevents the loading spinner when returning from booking view with cached data
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      try {
        // The assigned_to filter is passed directly to the Collins API as a
        // comma-separated string of user IDs. The Collins API handles this via
        // explode(',', $id) in BookingFilters.php:240, using whereIn to match
        // any of the specified users.
        const combinedFilters: FilterOptions = {
          ...filters,
          query: searchQuery || undefined
        };

        const enquiriesResponse = await fetchEnquiries({
          filters: combinedFilters,
          sort: sortOption,
          offset,
          limit,
          venueIds,
          venues
        });

        setEnquiries(enquiriesResponse.enquiries);
        setTotal(enquiriesResponse.total);
        // Use facet counts from the response - these are calculated from
        // unfiltered enquiries so sidebar counts remain accurate when filtering
        setFacetCounts(enquiriesResponse.facetCounts);
        setFilteredTotalHeads(enquiriesResponse.filteredTotalHeads);

        // Cache the fetched data for quick restoration when returning from booking view
        setCachedEnquiries({
          enquiries: enquiriesResponse.enquiries,
          total: enquiriesResponse.total,
          facetCounts: enquiriesResponse.facetCounts,
          filteredTotalHeads: enquiriesResponse.filteredTotalHeads
        });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch enquiries"
        );
      } finally {
        setLoading(false);
      }
    },
    [filters, sortOption, searchQuery, offset, limit, userId, venueIds, venues]
  );

  // Track if we have cached data to determine whether to show loading on initial fetch
  const hasCachedData = cachedData !== null;

  // Track whether this is the initial fetch so we can suppress the loading
  // spinner only on the very first render when cached data is available.
  // Subsequent fetches (pagination, filter / sort changes) must always show
  // the skeleton loader so users get visual feedback.
  const isInitialFetchRef = useRef(true);

  useEffect(() => {
    const showLoading = isInitialFetchRef.current ? !hasCachedData : true;
    isInitialFetchRef.current = false;
    fetchData(showLoading);
  }, [fetchData, hasCachedData]);

  useEffect(() => {
    setStoredValue(FILTERS_PREF_KEY, filters);
  }, [filters]);

  useEffect(() => {
    setStoredValue(SORT_PREF_KEY, sortOption);
  }, [sortOption]);

  // Sync enquiries list when selectedEnquiry is updated from SelectedBooking modal
  // This ensures the EnquiryCard icon updates when Take button is clicked in the modal
  useEffect(() => {
    if (
      contextSelectedEnquiry &&
      prevSelectedEnquiryRef.current?._id === contextSelectedEnquiry._id &&
      prevSelectedEnquiryRef.current?.status !== contextSelectedEnquiry.status
    ) {
      // The same enquiry was updated (e.g., status changed from 'new' to 'in_progress')
      setEnquiries((prev) =>
        prev.map((e) =>
          e._id === contextSelectedEnquiry._id
            ? { ...e, ...contextSelectedEnquiry }
            : e
        )
      );
      markAsRecentlyUpdated(contextSelectedEnquiry._id, setRecentlyUpdatedIds);
    }
    prevSelectedEnquiryRef.current = contextSelectedEnquiry;
  }, [contextSelectedEnquiry]);

  const updateFilter = useCallback(
    (key: keyof FilterOptions, value: unknown) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value
      }));
      setOffset(0);
    },
    []
  );

  /**
   * Apply multiple filter changes in a single batched setState call.
   * This prevents intermediate renders and potential race conditions
   * with debounced API calls when clearing or adjusting several filters
   * at once (e.g. clearing both date_from and date_to simultaneously).
   */
  const batchUpdateFilters = useCallback((updates: Partial<FilterOptions>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
    setOffset(0);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setOffset(0);
  }, []);

  const updateSort = useCallback((sort: EnquiriesSortOption) => {
    setSortOption(sort);
    setOffset(0);
  }, []);

  const updateSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setOffset(0);
  }, []);

  const onPaginate = useCallback((newOffset: number) => {
    setOffset(newOffset);
  }, []);

  const takeEnquiry = useCallback(
    async (enquiryId: string) => {
      if (!userId) {
        setError("User ID not available");
        return;
      }
      try {
        const updatedEnquiry = await takeEnquiryRequest(enquiryId, userId);
        setEnquiries((prev) =>
          prev.map((e) =>
            e._id === enquiryId ? { ...e, ...updatedEnquiry } : e
          )
        );
        markAsRecentlyUpdated(enquiryId, setRecentlyUpdatedIds);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to take enquiry");
      }
    },
    [userId]
  );

  const assignEnquiry = useCallback(
    async (enquiryId: string, userId: string | undefined) => {
      try {
        const updatedEnquiry = await assignEnquiryRequest(enquiryId, userId);
        setEnquiries((prev) =>
          prev.map((e) =>
            e._id === enquiryId ? { ...e, ...updatedEnquiry } : e
          )
        );
        markAsRecentlyUpdated(enquiryId, setRecentlyUpdatedIds);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to assign enquiry"
        );
      }
    },
    []
  );

  const updateStatus = useCallback(
    async (enquiryId: string, status: EnquiryStatus) => {
      try {
        const updatedEnquiry = await updateEnquiryStatusRequest(
          enquiryId,
          status
        );
        setEnquiries((prev) =>
          prev.map((e) =>
            e._id === enquiryId ? { ...e, ...updatedEnquiry } : e
          )
        );
        markAsRecentlyUpdated(enquiryId, setRecentlyUpdatedIds);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to update status"
        );
      }
    },
    []
  );

  /**
   * Clears open notifications for the selected enquiries.
   * Reference: collins-ui bookings.js clearSelectedBookingsNotifications (lines 401-496)
   *
   * 1. For each selected booking with embedded notifications (status='new'),
   *    calls the Core API to mark them as read.
   * 2. Handles virtual notification types (follow_up, untaken_two_hours, for_today)
   *    that have no embedded documents — removes from list without API call.
   * 3. Optimistically removes cleared bookings from the displayed list.
   * 4. Decrements facet counts for each cleared notification type.
   * 5. Re-fetches facet counts from the server after a delay for accuracy.
   */
  const enquiriesRef = useRef(enquiries);
  enquiriesRef.current = enquiries;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const scheduleFacetCountRefresh = (filtersForRefresh: FilterOptions) => {
    setTimeout(() => {
      fetchFacetCountsFromServer(filtersForRefresh).then((serverCounts) =>
        setFacetCounts(serverCounts)
      );
    }, 1500);
  };

  const applyFacetDecrements = (decrements: Record<string, number>) => {
    setFacetCounts((prev) => {
      if (!prev) return prev;
      const notifications = { ...prev.notifications };
      const notificationsIncRejected = { ...prev.notifications_inc_rejected };

      for (const [key, decrement] of Object.entries(decrements)) {
        if (key in notifications) {
          const typedKey = key as keyof typeof notifications;
          notifications[typedKey] = Math.max(
            0,
            notifications[typedKey] - decrement
          );
        }
        if (key in notificationsIncRejected) {
          const typedKey = key as keyof typeof notificationsIncRejected;
          notificationsIncRejected[typedKey] = Math.max(
            0,
            notificationsIncRejected[typedKey] - decrement
          );
        }
      }

      return {
        ...prev,
        notifications,
        notifications_inc_rejected: notificationsIncRejected
      };
    });
  };

  const clearNotifications = useCallback(async (selectedIds: Set<string>) => {
    const currentEnquiries = enquiriesRef.current;
    const currentFilters = filtersRef.current;
    const activeNotificationFilters = currentFilters.notifications ?? [];
    const acknowledgePromises: Promise<void>[] = [];
    const clearedBookingIds: string[] = [];
    const facetDecrements: Record<string, number> = {};

    const selectedEnquiries = currentEnquiries.filter((e) =>
      selectedIds.has(e._id)
    );

    for (const enquiry of selectedEnquiries) {
      const newNotifications = (enquiry.notifications ?? []).filter(
        (n) => n.status === "new"
      );
      const notificationTypes = [
        ...new Set(newNotifications.map((n) => n.type))
      ];

      if (newNotifications.length > 0) {
        clearedBookingIds.push(enquiry._id);

        acknowledgePromises.push(
          acknowledgeBookingNotifications(enquiry._id, notificationTypes)
        );

        for (const type of notificationTypes) {
          const facetKey = NOTIFICATION_FILTER_TO_FACET_KEY[type] ?? type;
          facetDecrements[facetKey] = (facetDecrements[facetKey] ?? 0) + 1;
        }
      } else if (
        activeNotificationFilters.some((f) => VIRTUAL_NOTIFICATION_TYPES.has(f))
      ) {
        clearedBookingIds.push(enquiry._id);
      }

      for (const filterValue of activeNotificationFilters) {
        if (VIRTUAL_NOTIFICATION_TYPES.has(filterValue)) {
          const facetKey =
            NOTIFICATION_FILTER_TO_FACET_KEY[filterValue] ?? filterValue;
          facetDecrements[facetKey] = (facetDecrements[facetKey] ?? 0) + 1;
        }
      }
    }

    setEnquiries((prev) =>
      prev.filter((e) => !clearedBookingIds.includes(e._id))
    );
    setTotal((prev) => Math.max(0, prev - clearedBookingIds.length));

    if (Object.keys(facetDecrements).length > 0) {
      applyFacetDecrements(facetDecrements);
    }

    const filtersForFacetCounts: FilterOptions = {
      ...currentFilters,
      status: undefined
    };

    try {
      await Promise.all(acknowledgePromises);
    } catch {
      setBulkActionError(
        "Failed to clear notifications for selected enquiries"
      );
    }

    scheduleFacetCountRefresh(filtersForFacetCounts);
  }, []);

  const bulkRejectEnquiries = useCallback(async (selectedIds: Set<string>) => {
    const ids = Array.from(selectedIds);
    try {
      await bulkReject(ids);
    } catch {
      setBulkActionError("Failed to reject selected enquiries");
      return;
    }
    setEnquiries((prev) => prev.filter((e) => !selectedIds.has(e._id)));
    setTotal((prev) => Math.max(0, prev - ids.length));
  }, []);

  const bulkDeleteEnquiries = useCallback(
    async (selectedIds: Set<string>, reason: BulkDeleteReason = "other") => {
      const ids = Array.from(selectedIds);
      try {
        await bulkDelete(ids, reason);
      } catch {
        setBulkActionError("Failed to delete selected enquiries");
        return;
      }
      setEnquiries((prev) => prev.filter((e) => !selectedIds.has(e._id)));
      setTotal((prev) => Math.max(0, prev - ids.length));
    },
    []
  );

  const bulkAssignEnquiries = useCallback(
    async (selectedIds: Set<string>, userId: string | undefined) => {
      const ids = Array.from(selectedIds);
      try {
        await bulkAssignTo(ids, userId ?? "");
      } catch {
        setBulkActionError("Failed to assign selected enquiries");
        return;
      }
      setEnquiries((prev) =>
        prev.map((e) =>
          selectedIds.has(e._id)
            ? {
                ...e,
                assigned_to: userId ?? undefined,
                assigned_to_name: undefined
              }
            : e
        )
      );
      for (const id of ids) {
        markAsRecentlyUpdated(id, setRecentlyUpdatedIds);
      }
    },
    []
  );

  const [bulkActionError, setBulkActionError] = useState<string | null>(null);
  const [isBulkApplying, setIsBulkApplying] = useState(false);

  const clearBulkActionError = useCallback(() => {
    setBulkActionError(null);
  }, []);

  const bulkApplyUpdates = useCallback(
    async (selectedIds: Set<string>, updates: BulkUpdateFields) => {
      const ids = Array.from(selectedIds);
      setIsBulkApplying(true);
      setBulkActionError(null);

      const operations: { label: string; promise: Promise<unknown> }[] = [];

      if (updates.status) {
        operations.push({
          label: "set status",
          promise: bulkSetStatus(ids, updates.status)
        });
      }
      if (updates.addLabel) {
        operations.push({
          label: "add action",
          promise: bulkAddLabels(ids, updates.addLabel)
        });
      }
      if (updates.removeLabel) {
        operations.push({
          label: "remove action",
          promise: bulkRemoveLabels(ids, updates.removeLabel)
        });
      }
      if (updates.followUp) {
        operations.push({
          label: "set follow-up",
          promise: bulkSetFollowUp(ids, updates.followUp)
        });
      }

      const results = await Promise.allSettled(
        operations.map((op) => op.promise)
      );

      const failed = results
        .map((result, i) =>
          result.status === "rejected" ? operations[i] : null
        )
        .filter(Boolean);

      if (failed.length > 0) {
        const failedLabels = failed.map((op) => op!.label).join(", ");
        setBulkActionError(`Failed to ${failedLabels}`);
        setIsBulkApplying(false);
        throw new Error(`Failed to ${failedLabels}`);
      }

      setIsBulkApplying(false);
      await fetchData();
    },
    [fetchData]
  );

  const refetch = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  return useMemo(
    () => ({
      enquiries,
      loading,
      error,
      filters,
      sortOption,
      searchQuery,
      facetCounts,
      filteredTotalHeads,
      total,
      offset,
      limit,
      selectedEnquiry,
      recentlyUpdatedIds,
      updateFilter,
      batchUpdateFilters,
      clearFilters,
      updateSort,
      updateSearch,
      onPaginate,
      takeEnquiry,
      updateStatus,
      assignEnquiry,
      clearNotifications,
      bulkRejectEnquiries,
      bulkDeleteEnquiries,
      bulkAssignEnquiries,
      bulkApplyUpdates,
      bulkActionError,
      clearBulkActionError,
      isBulkApplying,
      refetch,
      setLimit,
      setSelectedEnquiry
    }),
    [
      enquiries,
      loading,
      error,
      filters,
      sortOption,
      searchQuery,
      facetCounts,
      filteredTotalHeads,
      total,
      offset,
      limit,
      selectedEnquiry,
      recentlyUpdatedIds,
      updateFilter,
      batchUpdateFilters,
      clearFilters,
      updateSort,
      updateSearch,
      onPaginate,
      takeEnquiry,
      updateStatus,
      assignEnquiry,
      clearNotifications,
      bulkRejectEnquiries,
      bulkDeleteEnquiries,
      bulkAssignEnquiries,
      bulkApplyUpdates,
      bulkActionError,
      clearBulkActionError,
      isBulkApplying,
      refetch,
      setSelectedEnquiry
    ]
  );
};
